/**
 * Game-over podium scene.
 */

import type { PodiumRow } from '../state/types.js';

export interface PodiumHandle {
  setVisible(v: boolean): void;
  setPodium(rows: PodiumRow[]): void;
  dispose(): void;
}

const ROOT_ID = 'cm-podium';

export function createPodium(stage: HTMLElement): PodiumHandle {
  ensureStyles();
  const root = document.createElement('div');
  root.id = ROOT_ID;
  root.innerHTML = `
    <div class="cm-podium-grid">
      <h1 class="cm-podium-title">GAME OVER</h1>
      <ol class="cm-podium-list" data-cm-podium-list></ol>
    </div>
  `;
  stage.appendChild(root);

  function setVisible(v: boolean): void {
    root.style.display = v ? '' : 'none';
  }

  function setPodium(rows: PodiumRow[]): void {
    const list = root.querySelector('[data-cm-podium-list]');
    if (!list) return;
    if (!rows || rows.length === 0) {
      list.innerHTML = `<li class="cm-podium-empty">No scores this game</li>`;
      return;
    }
    list.innerHTML = rows
      .slice(0, 5)
      .map(
        (r) => `
          <li class="cm-podium-row rank-${r.rank}">
            <span class="cm-podium-rank">${r.rank}</span>
            <span class="cm-podium-name">${escapeHtml(r.display_name)}</span>
            <span class="cm-podium-pts">${r.total_points.toLocaleString('en-US')} pts</span>
          </li>
        `,
      )
      .join('');
  }

  return {
    setVisible,
    setPodium,
    dispose() {
      root.remove();
    },
  };
}

function ensureStyles(): void {
  if (document.getElementById('cm-podium-style')) return;
  const s = document.createElement('style');
  s.id = 'cm-podium-style';
  s.textContent = `
    #${ROOT_ID} {
      position: absolute;
      inset: 0;
      display: block;
    }
    #${ROOT_ID} .cm-podium-grid {
      display: grid;
      grid-template-rows: auto 1fr;
      gap: 40px;
      padding: 100px 240px;
      height: 100%;
      box-sizing: border-box;
      align-content: center;
      text-align: center;
    }
    #${ROOT_ID} .cm-podium-title {
      font-family: 'Bebas Neue', sans-serif;
      font-size: 240px;
      margin: 0;
      letter-spacing: 16px;
      background: linear-gradient(180deg, var(--sg-primary, #A855F7), var(--sg-accent, #EC4899));
      -webkit-background-clip: text;
      background-clip: text;
      color: transparent;
    }
    #${ROOT_ID} .cm-podium-list {
      list-style: none;
      padding: 0;
      margin: 0 auto;
      max-width: 900px;
      width: 100%;
    }
    #${ROOT_ID} .cm-podium-row {
      display: grid;
      grid-template-columns: 100px 1fr auto;
      align-items: center;
      gap: 24px;
      padding: 22px 32px;
      border-radius: 16px;
      background: rgba(31, 27, 75, 0.7);
      margin-bottom: 14px;
      font-size: 36px;
    }
    #${ROOT_ID} .cm-podium-row.rank-1 {
      background: linear-gradient(90deg, rgba(168, 85, 247, 0.3), rgba(236, 72, 153, 0.3));
      border: 2px solid var(--sg-celebration, #22C55E);
      transform: scale(1.04);
    }
    #${ROOT_ID} .cm-podium-rank {
      font-family: 'Bebas Neue', sans-serif;
      color: var(--sg-secondary, #06B6D4);
      font-size: 56px;
    }
    #${ROOT_ID} .cm-podium-name {
      color: #fff;
      text-align: left;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    #${ROOT_ID} .cm-podium-pts {
      color: var(--sg-celebration, #22C55E);
      font-family: 'Inter', sans-serif;
      font-weight: 700;
    }
  `;
  document.head.appendChild(s);
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
